import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CITY_MAP } from "./cities";
import { SITE } from "./config";
import Seo from "./Seo";
import { Phone, ArrowRight, Scale, Truck } from "lucide-react";

const PRICING_ROWS = [
  { matiere: "Gravats", prix: "35 €/t HT" },
  { matiere: "Plâtre & placo", prix: "160 €/t HT" },
  { matiere: "Mélange bitumineux", prix: "260 €/t HT" },
  { matiere: "Déchets verts", prix: "95 €/t HT" },
  { matiere: "Souches & troncs", prix: "175 €/t HT" },
  { matiere: "Bois", prix: "115 €/t HT" },
  { matiere: "DIB (cartons, plastiques)", prix: "245 €/t HT" },
  { matiere: "Archives confidentielles", prix: "sur devis" },
  { matiere: "Pneus VL", prix: "260 €/t HT" },
  { matiere: "Pneus PL", prix: "360 €/t HT" },
];

export default function TarifsVillePage() {
  const { slug } = useParams();
  const city = CITY_MAP[slug];
  if (!city) return <Navigate to="/" replace />;

  const url = `https://fbenvironnement.fr/tarifs/${city.slug}`;
  const title = `Tarifs déchèterie pro et benne à ${city.name} — FB Environnement`;
  const description = `Tarifs publics 2026 pour les pros de ${city.name} : gravats 35 €/t, plâtre 160 €/t, déchets verts 95 €/t. Prix benne sur devis. Centre à ${city.distanceKm} km.`;

  return (
    <div>
      <Seo title={title} description={description} path={`/tarifs/${city.slug}`} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://fbenvironnement.fr/" },
            { "@type": "ListItem", position: 2, name: `Tarifs ${city.name}`, item: url },
          ],
        })}</script>
      </Helmet>

      <section className="bg-gradient-to-br from-[#1F6B2E] to-[#0f3a18] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <nav className="text-sm mb-4 text-stone-200">
            <Link to="/" className="hover:text-[#9DCB2E]">Accueil</Link>
            <span className="mx-2 text-stone-400">/</span>
            <span className="text-white font-bold">Tarifs {city.name}</span>
          </nav>
          <span className="inline-block bg-[#9DCB2E] text-[#0f3a18] font-black uppercase tracking-[0.15em] text-xs px-3 py-1.5 rounded-full">Grille publique 2026</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-5">Tarifs déchèterie pro à {city.name}</h1>
          <p className="text-stone-100 text-lg mt-4 max-w-2xl">
            Prix transparents au tonnage pour les pros de {city.name}. Pas de frais cachés. Pesée à l&apos;arrivée et à la sortie au centre FB Environnement de Saint-Victoret ({city.distanceKm} km).
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-heading font-black text-3xl">Grille au tonnage — dépôt direct</h2>
        <p className="text-stone-600 mt-2">Tarifs applicables aux pros venant déposer à Saint-Victoret.</p>
        <div className="mt-7 bg-white border border-stone-100 rounded-3xl overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-stone-50">
              <tr><th className="px-5 py-3 text-xs uppercase tracking-wider font-bold text-stone-500">Matière</th><th className="px-5 py-3 text-xs uppercase tracking-wider font-bold text-stone-500 text-right">Tarif</th></tr>
            </thead>
            <tbody>
              {PRICING_ROWS.map((r) => (
                <tr key={r.matiere} className="border-t border-stone-100">
                  <td className="px-5 py-3 font-bold text-stone-900">{r.matiere}</td>
                  <td className="px-5 py-3 text-right font-heading font-black text-[#1F6B2E]">{r.prix}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-stone-500 mt-3">Tarifs HT, hors TVA 20%. Pesée minimale facturée selon barème.</p>
      </section>

      <section className="bg-stone-50 py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading font-black text-3xl">Location de benne livrée à {city.name}</h2>
          <p className="text-stone-600 mt-2">Prix sur devis. Inclut : livraison, location pour la durée convenue, retrait, traitement et bordereau de suivi.</p>
          <div className="grid sm:grid-cols-3 gap-4 mt-7">
            {[
              { v: "3-5 m³", u: "petits travaux" },
              { v: "8-10 m³", u: "rénovation appartement" },
              { v: "15-20 m³", u: "rénovation maison / démolition" },
            ].map((b) => (
              <div key={b.v} className="bg-white rounded-2xl p-5 border border-stone-100">
                <Truck className="w-7 h-7 text-[#1F6B2E] mb-3" />
                <div className="font-heading font-black text-2xl">{b.v}</div>
                <p className="text-sm text-stone-600 mt-1">{b.u}</p>
                <p className="text-xs text-stone-500 mt-2">Devis personnalisé selon distance + matière.</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={`/benne/${city.slug}`} className="inline-flex items-center gap-2 bg-[#1F6B2E] hover:bg-[#0f3a18] text-white font-bold px-5 py-3 rounded-full"><Truck className="w-4 h-4" /> Bennes à {city.name}</Link>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white border border-stone-200 text-[#0f3a18] font-bold px-5 py-3 rounded-full">Devis sur mesure <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="bg-white border border-stone-100 rounded-3xl p-6 flex items-start gap-4">
          <Scale className="w-10 h-10 text-[#1F6B2E] shrink-0" strokeWidth={2.5} />
          <div>
            <div className="font-heading font-black text-xl">Comment on calcule votre prix à {city.name} ?</div>
            <p className="text-stone-600 mt-2">3 facteurs : la <strong>matière</strong>, le <strong>volume / tonnage</strong>, et le <strong>conditionnement</strong> (dépôt au centre ou benne livrée). Pour un même tonnage, le dépôt direct au centre est toujours le plus économique. Pour un gros chantier ou une rénovation lourde, la benne est plus pratique.</p>
            <Link to="/comparatif" className="inline-flex items-center gap-1 mt-3 text-[#1F6B2E] font-bold">Centre vs Benne — quel est le moins cher ? <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="bg-gradient-to-br from-[#9DCB2E] to-[#7ba81f] rounded-3xl p-8 sm:p-12 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-[#0f3a18]">Un devis précis à {city.name} ?</h2>
          <p className="text-[#0f3a18]/80 mt-3">2 minutes au téléphone et c&apos;est calé.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <a href={`tel:${SITE.phoneRaw}`} className="inline-flex items-center gap-2 bg-[#0f3a18] text-white font-bold px-6 py-3 rounded-full"><Phone className="w-4 h-4" /> {SITE.phone}</a>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-[#0f3a18] font-bold px-6 py-3 rounded-full">Devis en ligne <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
